// Reorder product and card gallery media based on URL/referrer gender context
// - Detects preferred gender ("mens" or "womens") from current path, referrer, or sessionStorage
// - Reorders slides in product media galleries and product card galleries so that
//   matching images (by filename containing the gender token and an optional order number)
//   are moved to the front, ordered by the number when present
// - Keeps non-matching media in their original relative order
// - Reapplies on variant updates and when new galleries are added to the DOM

import { ThemeEvents } from '@theme/events';

/** @typedef {HTMLElement & { dataset: DOMStringMap }} HTMLElementWithDataset */

const TOGGLE_SELECTOR = '[data-gender-toggle]';
const STORAGE_KEY = 'genderPreference';
const EXCLUDED_COLLECTION_SEGMENTS = ['/bags', '/jewelry', '/footwear', '/accesories', '/accessories', '/mystery-boxes'];
/**
 * Current in-page preference (not persisted across navigation).
 * @type {('mens'|'womens'|null)}
 */
let currentPreference = null;
let appliedStoredPreference = false;

/** Determine if current page is a product page */
function isProductPage() {
  const path = (location.pathname || '').toLowerCase();
  return path.includes('/products/');
}

/** Get preference from URL path if present */
function getPreferenceFromPath() {
  const path = (location.pathname || '').toLowerCase();
  if (path.includes('/mens')) return /** @type any */ ('mens');
  if (path.includes('/womens')) return /** @type any */ ('womens');
  return null;
}

/** Apply stored preference once on product pages (for this navigation only) */
function applyStoredPreferenceForProductPage() {
  if (appliedStoredPreference) return;
  if (currentPreference) { appliedStoredPreference = true; return; }
  if (!isProductPage()) return;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === 'mens' || stored === 'womens') {
      currentPreference = /** @type any */ (stored);
    }
    // Clear it so it doesn't leak to future navigations unless reselected
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
  appliedStoredPreference = true;
}
/**
 * Keep track of the original child order for containers we mutate,
 * so we can restore it when the selected gender has no matches.
 * Keys are containers like <slideshow-slides> and .media-gallery__grid.
 * Values are arrays of the original child Elements.
 * WeakMap ensures no memory leaks when nodes are removed.
 */
const originalOrderMap = new WeakMap();

/**
 * Determine preferred gender from sessionStorage only.
 * URL and referrer are intentionally ignored to keep the toggle unselected by default.
 * @returns {('mens'|'womens'|null)}
 */
function getPreferredGender() {
  return /** @type any */ (currentPreference);
}

/**
 * Extract filename from an image element (without query params).
 * @param {HTMLImageElement} img
 */
function getImageFileName(img) {
  // Use currentSrc if available to account for srcset
  const raw = img.currentSrc || img.src || '';
  try {
    const url = new URL(raw, location.origin);
    const pathname = url.pathname || '';
    const last = pathname.substring(pathname.lastIndexOf('/') + 1);
    return last.toLowerCase();
  } catch (_) {
    // If URL fails to parse, best-effort fallback
    const base = (raw || '').split('?')[0] || '';
    const idx = base.lastIndexOf('/') + 1;
    const name = idx >= 0 ? base.substring(idx) : base;
    return (name || '').toLowerCase();
  }
}

/**
 * Parse 'm' and 'w' tokens (and optional order numbers) from a filename.
 * Returns the order for each gender independently so filenames like "..._m1_w10.jpg"
 * can match both selections appropriately.
 * - undefined: no token for that gender was found
 * - null: token found but no number
 * - number: token found with parsed order
 * @param {string} filenameLower
 * @returns {{ mens: number|null|undefined, womens: number|null|undefined }}
 */
function parseGenderOrdersFromFilename(filenameLower) {
  const mensRe = /(?:^|[^a-z0-9])m(?:[-_]*(\d{1,3}))?(?=[^a-z0-9]|$)/;
  const womensRe = /(?:^|[^a-z0-9])w(?:[-_]*(\d{1,3}))?(?=[^a-z0-9]|$)/;

  const mensMatch = filenameLower.match(mensRe);
  const womensMatch = filenameLower.match(womensRe);

  /** @type {number|null|undefined} */
  let mensOrder = undefined;
  let womensOrder = undefined;

  if (mensMatch) {
    const order = mensMatch[1] ? parseInt(mensMatch[1], 10) : null;
    mensOrder = Number.isFinite(order) ? order : null;
  }

  if (womensMatch) {
    const order = womensMatch[1] ? parseInt(womensMatch[1], 10) : null;
    womensOrder = Number.isFinite(order) ? order : null;
  }

  return { mens: mensOrder, womens: womensOrder };
}

/**
 * Returns true if any element in the list has an <img> whose filename
 * parses to the preferred gender. Used to skip reordering when there
 * are no matching images for the chosen gender.
 * @param {Element[]} items
 * @param {('mens'|'womens')} preferred
 */
function anyItemMatchesPreferredGender(items, preferred) {
  for (const el of items) {
    const img = /** @type {HTMLImageElement|null} */ (el.querySelector('img'));
    if (!img) continue;
    const filename = getImageFileName(img);
    const orders = parseGenderOrdersFromFilename(filename);
    if (preferred === 'mens' ? orders.mens !== undefined : orders.womens !== undefined) return true;
  }
  return false;
}

/**
 * Store the original child order of a container once.
 * @param {HTMLElement} container
 */
function ensureOriginalOrderStored(container) {
  if (!originalOrderMap.has(container)) {
    originalOrderMap.set(container, Array.from(container.children));
  }
}

/**
 * Apply the previously stored original order to a grid container (product media grid).
 * Also renumbers zoom button indices.
 * @param {HTMLElement} grid
 */
function restoreGridOriginalOrder(grid) {
  const original = /** @type {Element[]|undefined} */ (originalOrderMap.get(grid));
  if (!original || !original.length) return;
  const current = Array.from(grid.children);
  if (!original.every((el, i) => el === current[i])) {
    applyNewOrder(grid, original);
  }
  // Renumber any zoom buttons inside grid items: on:click="/zoom/<index>"
  Array.from(grid.children).forEach((li, i) => {
    if (!(li instanceof HTMLElement)) return;
    const zoomBtn = li.querySelector('.product-media-container__zoom-button[on\\:click]');
    if (zoomBtn instanceof HTMLElement) {
      const clickAttr = zoomBtn.getAttribute('on:click');
      if (typeof clickAttr === 'string' && /(\/zoom\/)\d+$/.test(clickAttr)) {
        zoomBtn.setAttribute('on:click', clickAttr.replace(/(\/zoom\/)\d+$/, `$1${i}`));
      }
    }
  });
}

/**
 * Restore the original order for a slideshow-component and its related lists.
 * @param {HTMLElement} slideshowComponent
 */
function restoreSlideshowOriginalOrder(slideshowComponent) {
  const slidesContainer = slideshowComponent.querySelector('slideshow-slides');
  if (slidesContainer instanceof HTMLElement) {
    const originalSlides = /** @type {Element[]|undefined} */ (originalOrderMap.get(slidesContainer));
    if (originalSlides && originalSlides.length) {
      const currentSlides = Array.from(slidesContainer.querySelectorAll('slideshow-slide'));
      if (!originalSlides.every((el, i) => el === currentSlides[i])) {
        applyNewOrder(slidesContainer, originalSlides);
      }
      // Normalize slide attributes to match indices and update zoom open indices
      const updatedSlides = Array.from(slidesContainer.querySelectorAll('slideshow-slide'));
      updatedSlides.forEach((slide, index) => {
        if (!(slide instanceof HTMLElement)) return;
        slide.style.setProperty('view-timeline-name', `--slide-${index}`);
        slide.setAttribute('aria-hidden', `${index !== 0}`);
        const zoomOpen = slide.getAttribute('on:click');
        if (typeof zoomOpen === 'string' && /(open\/)\d+$/.test(zoomOpen)) {
          slide.setAttribute('on:click', zoomOpen.replace(/(open\/)\d+$/, `$1${index}`));
        }
      });
    }
  }

  // Restore thumbnails order if present
  const thumbList = slideshowComponent.querySelector('.slideshow-controls__thumbnails');
  if (thumbList instanceof HTMLElement) {
    ensureOriginalOrderStored(thumbList);
    const originalThumbs = /** @type {Element[]|undefined} */ (originalOrderMap.get(thumbList));
    if (originalThumbs && originalThumbs.length) {
      const currentThumbs = Array.from(thumbList.children);
      if (!originalThumbs.every((el, i) => el === currentThumbs[i])) {
        applyNewOrder(thumbList, originalThumbs);
      }
      const buttons = Array.from(thumbList.querySelectorAll('button'));
      const total = buttons.length;
      buttons.forEach((btn, i) => {
        if (!(btn instanceof HTMLElement)) return;
        const clickAttr = btn.getAttribute('on:click');
        if (typeof clickAttr === 'string' && /(select\/)\d+$/.test(clickAttr)) {
          btn.setAttribute('on:click', clickAttr.replace(/(select\/)\d+$/, `$1${i}`));
        }
        btn.setAttribute('aria-label', `Slide ${i + 1} of ${total}`);
      });
    }
  }

  // Restore zoom lists if present
  const mediaGallery = slideshowComponent.closest('media-gallery');
  if (mediaGallery) {
    const zoomList = mediaGallery.querySelector('.dialog-zoomed-gallery');
    if (zoomList instanceof HTMLElement) {
      ensureOriginalOrderStored(zoomList);
      const originalZoom = /** @type {Element[]|undefined} */ (originalOrderMap.get(zoomList));
      if (originalZoom && originalZoom.length) {
        const currentZoom = Array.from(zoomList.children);
        if (!originalZoom.every((el, i) => el === currentZoom[i])) {
          applyNewOrder(zoomList, originalZoom);
        }
      }
    }
    const zoomThumbs = mediaGallery.querySelector('.dialog-thumbnails-list');
    if (zoomThumbs instanceof HTMLElement) {
      ensureOriginalOrderStored(zoomThumbs);
      const originalZoomThumbs = /** @type {Element[]|undefined} */ (originalOrderMap.get(zoomThumbs));
      if (originalZoomThumbs && originalZoomThumbs.length) {
        const currentZoomThumbs = Array.from(zoomThumbs.children);
        if (!originalZoomThumbs.every((el, i) => el === currentZoomThumbs[i])) {
          applyNewOrder(zoomThumbs, originalZoomThumbs);
        }
        // Renumber zoom thumbnail event indices
        Array.from(zoomThumbs.querySelectorAll('button')).forEach((btn, i) => {
          if (!(btn instanceof HTMLElement)) return;
          const clickAttr = btn.getAttribute('on:click');
          if (typeof clickAttr === 'string' && /(handleThumbnailClick\/)\d+$/.test(clickAttr)) {
            btn.setAttribute('on:click', clickAttr.replace(/(handleThumbnailClick\/)\d+$/, `$1${i}`));
          }
          const enterAttr = btn.getAttribute('on:pointerenter');
          if (typeof enterAttr === 'string' && /(handleThumbnailPointerEnter\/)\d+$/.test(enterAttr)) {
            btn.setAttribute('on:pointerenter', enterAttr.replace(/(handleThumbnailPointerEnter\/)\d+$/, `$1${i}`));
          }
        });
      }
    }
  }

  const anySlideshow = /** @type {any} */ (slideshowComponent);
  try {
    if (typeof anySlideshow.updatedCallback === 'function') anySlideshow.updatedCallback();
  } catch (_) {}
  queueMicrotask(() => {
    try {
      if (typeof anySlideshow.select === 'function') anySlideshow.select(0, undefined, { animate: false });
    } catch (_) {}
    reinitCustomElement(slideshowComponent);
  });
}

/**
 * Given a list of elements, compute a stable order that prioritizes preferred gender.
 * @param {Element[]} items - Elements representing media items (slides or li)
 * @param {('mens'|'womens')} preferred
 * @returns {Element[]} sorted elements
 */
function sortByGender(items, preferred) {
  /** @type {{ el: Element, group: number, order: number, idx: number }[]} */
  const enriched = items.map((el, idx) => {
    // Try to find an image within this element
    const img = /** @type {HTMLImageElement|null} */ (el.querySelector('img'));
    if (!img) return { el, group: 1, order: Number.POSITIVE_INFINITY, idx };

    const filename = getImageFileName(img);
    const orders = parseGenderOrdersFromFilename(filename);
    const selectedOrder = preferred === 'mens' ? orders.mens : orders.womens;

    // group 0: matches preferred gender; group 1: everything else
    const group = selectedOrder !== undefined ? 0 : 1;
    // Within group 0, order by number if present, otherwise by original index
    const numeric = selectedOrder ?? Number.POSITIVE_INFINITY;
    return { el, group, order: numeric, idx };
  });

  enriched.sort((a, b) => {
    if (a.group !== b.group) return a.group - b.group;
    if (a.group === 0 && a.order !== b.order) return a.order - b.order;
    return a.idx - b.idx; // stable fallback
  });

  return enriched.map((x) => x.el);
}

/**
 * Reorder children of a container to match a new order array.
 * @param {HTMLElement} container
 * @param {Element[]} newOrder
 */
function applyNewOrder(container, newOrder) {
  const frag = document.createDocumentFragment();
  newOrder.forEach((node) => frag.appendChild(node));
  container.appendChild(frag);
}

/**
 * Force re-running connectedCallback by detaching and re-attaching the element in place.
 * @param {HTMLElement} el
 */
function reinitCustomElement(el) {
  const parent = el.parentNode;
  if (!parent) return;
  const next = el.nextSibling;
  parent.removeChild(el);
  if (next) parent.insertBefore(el, next);
  else parent.appendChild(el);
}

/**
 * Reorder a single slideshow-component (slides and its thumbnails if present) by gender.
 * @param {HTMLElement} slideshowComponent
 * @param {('mens'|'womens')} preferred
 */
function reorderSlideshow(slideshowComponent, preferred) {
  const slidesContainer = slideshowComponent.querySelector('slideshow-slides');
  if (!(slidesContainer instanceof HTMLElement)) return;

  const slides = Array.from(slidesContainer.querySelectorAll('slideshow-slide'));
  if (!slides.length) return;

  // Store original slides order once for potential restoration
  ensureOriginalOrderStored(slidesContainer);

  // Skip reordering if there are no images matching the preferred gender; restore original if previously reordered
  if (!anyItemMatchesPreferredGender(slides, preferred)) {
    restoreSlideshowOriginalOrder(slideshowComponent);
    return;
  }

  const newSlidesOrder = sortByGender(slides, preferred);
  // If nothing changes, bail
  if (newSlidesOrder.every((el, i) => el === slides[i])) return;

  applyNewOrder(slidesContainer, newSlidesOrder);

  // Renumber slides: update timeline name, aria-hidden, and any zoom on:click indices
  const count = newSlidesOrder.length;
  newSlidesOrder.forEach((slide, index) => {
    if (!(slide instanceof HTMLElement)) return;
    // Ensure timeline variable aligns with new index
    slide.style.setProperty('view-timeline-name', `--slide-${index}`);
    // Reset visibility; slideshow will sync after selection, but this avoids mismatches
    slide.setAttribute('aria-hidden', `${index !== 0}`);

    // Update slide zoom-open handler if present (e.g., on:click="#zoom-dialog-<id>/open/<index>")
    const zoomOpen = slide.getAttribute('on:click');
    if (typeof zoomOpen === 'string' && /(open\/)\d+$/.test(zoomOpen)) {
      slide.setAttribute('on:click', zoomOpen.replace(/(open\/)\d+$/, `$1${index}`));
    }
  });

  // Attempt to reset scroller position to first slide immediately
  try {
    slidesContainer.scrollLeft = 0;
  } catch (_) {}

  // Reorder thumbnails in slideshow controls if present
  const thumbList = slideshowComponent.querySelector('.slideshow-controls__thumbnails');
  if (thumbList instanceof HTMLElement) {
    const buttons = Array.from(thumbList.querySelectorAll('button'));
    if (buttons.length === slides.length) {
      // Create a mapping from old index to new index based on slide elements
      const indexMap = new Map();
      newSlidesOrder.forEach((el, newIdx) => {
        const oldIdx = slides.indexOf(el);
        indexMap.set(oldIdx, newIdx);
      });
      const newButtonsOrder = newSlidesOrder
        .map((el) => {
          const idx = slides.indexOf(el);
          return idx >= 0 ? buttons[idx] : undefined;
        })
        .filter((x) => x instanceof Element);
      applyNewOrder(thumbList, /** @type {Element[]} */ (newButtonsOrder));

      // Renumber thumbnail buttons: update on:click index and aria-label
      const total = newButtonsOrder.length;
      newButtonsOrder.forEach((btn, i) => {
        if (!(btn instanceof HTMLElement)) return;
        const clickAttr = btn.getAttribute('on:click');
        if (typeof clickAttr === 'string' && /(select\/)\d+$/.test(clickAttr)) {
          btn.setAttribute('on:click', clickAttr.replace(/(select\/)\d+$/, `$1${i}`));
        }
        // Normalize label to match new index; fallback to English string
        btn.setAttribute('aria-label', `Slide ${i + 1} of ${total}`);
      });
    }
  }

  // Reorder Zoom Dialog lists if present within the same media-gallery
  const mediaGallery = slideshowComponent.closest('media-gallery');
  if (mediaGallery) {
    // Zoomed gallery main list
    const zoomList = mediaGallery.querySelector('.dialog-zoomed-gallery');
    if (zoomList instanceof HTMLElement) {
      const zoomItems = Array.from(zoomList.children);
      if (zoomItems.length === slides.length) {
        const newZoomOrder = newSlidesOrder
          .map((el) => {
            const oldIdx = slides.indexOf(el);
            return oldIdx >= 0 ? zoomItems[oldIdx] : undefined;
          })
          .filter((x) => x instanceof Element);
        applyNewOrder(zoomList, /** @type {Element[]} */ (newZoomOrder));
      }
    }
    // Zoom thumbnails list
    const zoomThumbs = mediaGallery.querySelector('.dialog-thumbnails-list');
    if (zoomThumbs instanceof HTMLElement) {
      const zoomButtons = Array.from(zoomThumbs.querySelectorAll('button'));
      if (zoomButtons.length === slides.length) {
        const newZoomBtnsOrder = /** @type {Element[]} */ (
          newSlidesOrder
            .map((el) => {
              const oldIdx = slides.indexOf(el);
              return oldIdx >= 0 ? zoomButtons[oldIdx] : undefined;
            })
            .filter((x) => x instanceof Element)
        );
        applyNewOrder(zoomThumbs, newZoomBtnsOrder);

        // Renumber zoom thumbnail event indices
        newZoomBtnsOrder.forEach((btn, i) => {
          if (!(btn instanceof HTMLElement)) return;
          const clickAttr = btn.getAttribute('on:click');
          if (typeof clickAttr === 'string' && /(handleThumbnailClick\/)\d+$/.test(clickAttr)) {
            btn.setAttribute('on:click', clickAttr.replace(/(handleThumbnailClick\/)\d+$/, `$1${i}`));
          }
          const enterAttr = btn.getAttribute('on:pointerenter');
          if (typeof enterAttr === 'string' && /(handleThumbnailPointerEnter\/)\d+$/.test(enterAttr)) {
            btn.setAttribute('on:pointerenter', enterAttr.replace(/(handleThumbnailPointerEnter\/)\d+$/, `$1${i}`));
          }
        });
      }
    }
  }

  // After reordering, force the component to refresh refs, then reselect first slide by id
  const anySlideshow = /** @type {any} */ (slideshowComponent);
  try {
    if (typeof anySlideshow.updatedCallback === 'function') anySlideshow.updatedCallback();
  } catch (_) {}

  queueMicrotask(() => {
    const firstSlide = /** @type {HTMLElement|null} */ (newSlidesOrder[0] instanceof HTMLElement ? newSlidesOrder[0] : null);
    const firstId = firstSlide?.getAttribute('slide-id');
    try {
      if (firstId && typeof anySlideshow.select === 'function') {
        anySlideshow.select({ id: firstId }, undefined, { animate: false });
      } else if (typeof anySlideshow.select === 'function') {
        anySlideshow.select(0, undefined, { animate: false });
      } else {
        // Fallback: nudge initial-slide attribute to trigger sync
        const current = slideshowComponent.getAttribute('initial-slide') || '0';
        slideshowComponent.setAttribute('initial-slide', current === '0' ? '1' : '0');
        slideshowComponent.setAttribute('initial-slide', '0');
      }
    } catch (_) {}

    // As a last resort, re-initialize the slideshow element to refresh internal state
    reinitCustomElement(slideshowComponent);
  });
}

/**
 * Reorder a product media gallery element by gender (carousel or grid).
 * @param {HTMLElement} mediaGallery
 * @param {('mens'|'womens')} preferred
 */
function reorderMediaGallery(mediaGallery, preferred) {
  // Carousel mode: slideshow-component inside
  const slideshow = mediaGallery.querySelector('slideshow-component');
  if (slideshow instanceof HTMLElement) {
    reorderSlideshow(slideshow, preferred);
    return;
  }

  // Grid mode: reorder list items in media-gallery__grid
  const grid = mediaGallery.querySelector('.media-gallery__grid');
  if (grid instanceof HTMLElement) {
    const items = Array.from(grid.children);
    // Store original grid order once for potential restoration
    ensureOriginalOrderStored(grid);
    // Skip reordering if there are no images matching the preferred gender; restore original if previously reordered
    if (!anyItemMatchesPreferredGender(items, preferred)) {
      restoreGridOriginalOrder(grid);
      return;
    }

    const reordered = sortByGender(items, preferred);
    if (!reordered.every((el, i) => el === items[i])) {
      applyNewOrder(grid, reordered);

      // Renumber any zoom buttons inside grid items: on:click="/zoom/<index>"
      Array.from(grid.children).forEach((li, i) => {
        if (!(li instanceof HTMLElement)) return;
        const zoomBtn = li.querySelector('.product-media-container__zoom-button[on\\:click]');
        if (zoomBtn instanceof HTMLElement) {
          const clickAttr = zoomBtn.getAttribute('on:click');
          if (typeof clickAttr === 'string' && /(\/zoom\/)\d+$/.test(clickAttr)) {
            zoomBtn.setAttribute('on:click', clickAttr.replace(/(\/zoom\/)\d+$/, `$1${i}`));
          }
        }
      });
    }
  }
}

/**
 * Reorder all card galleries on the page by gender.
 * @param {('mens'|'womens')} preferred
 */
function reorderCardGalleries(preferred) {
  const cards = document.querySelectorAll('.card-gallery');
  for (const card of cards) {
    const slideshow = card.querySelector('slideshow-component');
    if (slideshow instanceof HTMLElement) reorderSlideshow(slideshow, preferred);
  }
}

/** Reorders all known galleries based on current preference. */
function reorderAll() {
  const preferred = getPreferredGender();
  if (!preferred) {
    // No preference => no reordering needed; ensure galleries are visible
    document.querySelectorAll('media-gallery[data-gender-auto-reorder]').forEach((el) => {
      if (el instanceof HTMLElement) el.setAttribute('data-gender-ready', 'true');
    });
    document.querySelectorAll('.card-gallery[data-gender-auto-reorder]').forEach((el) => {
      if (el instanceof HTMLElement) el.setAttribute('data-gender-ready', 'true');
    });
    return;
  }

  const mediaGalleries = document.querySelectorAll('media-gallery');
  for (const gallery of mediaGalleries) {
    reorderMediaGallery(/** @type {HTMLElement} */ (gallery), preferred);
    // Mark ready to un-hide as soon as this gallery processed
    if (gallery instanceof HTMLElement && gallery.hasAttribute('data-gender-auto-reorder')) {
      gallery.setAttribute('data-gender-ready', 'true');
    }
  }

  reorderCardGalleries(preferred);
  // Unhide all card galleries after processing
  document.querySelectorAll('.card-gallery[data-gender-auto-reorder]').forEach((el) => {
    if (el instanceof HTMLElement) el.setAttribute('data-gender-ready', 'true');
  });
}

function setupObservers() {
  // Reapply after variant updates (media-gallery replaces itself)
  document.addEventListener(ThemeEvents.variantUpdate, () => {
    // Allow the gallery replacement to complete
    setTimeout(reorderAll, 0);
  });

  // Observe added nodes that might include galleries (e.g., filter/morph updates)
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      // Handle newly added elements that contain galleries or toggles
      if (m.type === 'childList') {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;
          const hasGalleries =
            node.matches('media-gallery, .card-gallery') || node.querySelector?.('media-gallery, .card-gallery');
          const hasToggle = node.matches(TOGGLE_SELECTOR) || node.querySelector?.(TOGGLE_SELECTOR);
          if (hasGalleries || hasToggle) {
            initGenderToggleUI();
            reorderAll();
            return;
          }
        }
      }

      // Handle attribute changes where data-gender-ready is removed during morphing
      if (m.type === 'attributes' && m.attributeName === 'data-gender-ready') {
        const target = m.target;
        if (!(target instanceof Element)) continue;
        const isAutoReorderGallery = target.matches(
          'media-gallery[data-gender-auto-reorder], .card-gallery[data-gender-auto-reorder]'
        );
        // If the ready flag was removed from an auto-reorder gallery, re-apply ordering to restore visibility
        if (isAutoReorderGallery && !target.hasAttribute('data-gender-ready')) {
          initGenderToggleUI();
          reorderAll();
          return;
        }
      }
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-gender-ready'],
  });
}

function init() {
  // 1) Preselect from URL when present and persist so it carries over to product pages
  const pathPref = getPreferenceFromPath();
  if (pathPref) {
    currentPreference = pathPref;
    try { sessionStorage.setItem(STORAGE_KEY, pathPref); } catch (_) {}
  }

  // 2) If arriving on a product page without a URL hint, apply stored preference once
  applyStoredPreferenceForProductPage();
  initGenderToggleUI();
  reorderAll();
  setupObservers();
  // 3) On product pages, we don't need to keep the stored preference after applying
  try { if (isProductPage()) sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
}

/**
 * Initialize gender toggle controls on collection pages.
 */
function initGenderToggleUI() {
  // Only relevant on pages that include /collections in the URL
  const pathLower = (location.pathname || '').toLowerCase();
  const onCollections = pathLower.includes('/collections');
  if (!onCollections) return;

  const containers = document.querySelectorAll(TOGGLE_SELECTOR);
  if (!containers.length) return;

  // Exclude specific collection segments from showing the toggle
  if (EXCLUDED_COLLECTION_SEGMENTS.some((seg) => pathLower.includes(seg))) {
    containers.forEach((container) => {
      if (container instanceof HTMLElement) container.style.display = 'none';
    });
    return;
  }

  containers.forEach((container) => {
    const current = getPreferredGender();
    // Reflect state (no default selection → all false when null)
    for (const btn of container.querySelectorAll('[data-gender-option]')) {
      if (!(btn instanceof HTMLElement)) continue;
      const value = (btn.getAttribute('data-gender-option') || '').toLowerCase();
      const pressed = current ? value === current : false;
      btn.setAttribute('aria-pressed', String(pressed));
    }

    // Setup event listeners once per instance
    if (!container.hasAttribute('data-initialized')) {
      container.setAttribute('data-initialized', 'true');
      container.addEventListener('click', (e) => {
        const target = e.target instanceof Element ? e.target.closest('[data-gender-option]') : null;
        if (!target || !(target instanceof HTMLElement)) return;
        const value = (target.getAttribute('data-gender-option') || '').toLowerCase();
        if (value !== 'mens' && value !== 'womens') return;

        // Set in-page preference and persist only for product pages (so product galleries can reorder)
        currentPreference = /** @type any */ (value);
        try {
          sessionStorage.setItem(STORAGE_KEY, value);
        } catch (_) {}

        for (const btn of container.querySelectorAll('[data-gender-option]')) {
          if (!(btn instanceof HTMLElement)) continue;
          const isActive = (btn.getAttribute('data-gender-option') || '').toLowerCase() === value;
          btn.setAttribute('aria-pressed', String(isActive));
        }

        // Apply immediately
        reorderAll();
      });
    }
  });
}

// Run as early as possible to avoid flicker: if DOM isn't ready, run on DOMContentLoaded, else immediately
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
